<template>
  <section id="voucher-config" class="page-section vouchers-section selection-section">
    <div class="vouchers-scroll-shell">
      <div class="vouchers-scroll-region" role="region" aria-label="Winner screen customization section">
        <div class="vouchers-content">
          <div class="section-header">
            <h3>Customise your rewards page</h3>
          </div>

          <div class="phone-preview-shell">
            <div class="phone-preview">
              <div class="phone-speaker"></div>
              <div class="phone-screen" :style="phoneScreenStyle">
                <button
                  type="button"
                  :class="['tap-target', 'logo-target', { 'preview-target--configured': hasCustomizedLogo }]"
                  aria-label="Customize logo"
                  @click="emit('open-logo-customizer')"
                >
                  <svg class="marching-ants marching-ants--logo" viewBox="0 0 220 88" preserveAspectRatio="none" aria-hidden="true">
                    <rect x="1" y="1" width="218" height="86" rx="14" ry="14" pathLength="100" />
                  </svg>
                  <img :src="resolvedLogoSrc" alt="Campaign logo preview" class="phone-logo" />
                </button>

                <button
                  type="button"
                  :class="['tap-target', 'message-target', { 'message-target--placeholder': !hasCustomMessage, 'preview-target--configured': hasCustomMessage }]"
                  aria-label="Customize prize message"
                  @click="openMessageModal"
                >
                  <svg class="marching-ants marching-ants--panel" viewBox="0 0 220 132" preserveAspectRatio="none" aria-hidden="true">
                    <rect x="1" y="1" width="218" height="130" rx="16" ry="16" pathLength="100" />
                  </svg>
                  <p
                    class="preview-message"
                    :class="{ 'preview-message--placeholder': !hasCustomMessage }"
                    :style="phoneMessageStyle"
                  >
                    {{ previewMessage }}
                  </p>
                </button>

                <button
                  type="button"
                  :class="['tap-target', 'voucher-target', { 'voucher-target--placeholder': !hasConfiguredVouchers, 'preview-target--configured': hasConfiguredVouchers }]"
                  aria-label="Customize voucher setup"
                  @click="openVoucherModal"
                >
                  <svg class="marching-ants marching-ants--panel" viewBox="0 0 220 104" preserveAspectRatio="none" aria-hidden="true">
                    <rect x="1" y="1" width="218" height="102" rx="16" ry="16" pathLength="100" />
                  </svg>
                  <p
                    class="preview-voucher"
                    :class="{ 'preview-voucher--placeholder': !hasConfiguredVouchers }"
                  >
                    {{ previewVoucherSummary }}
                  </p>
                </button>

                <div :class="['cta-target', { 'preview-target--configured': hasCustomizedPrizeButton }]">
                  <svg class="marching-ants marching-ants--pill" viewBox="0 0 220 56" preserveAspectRatio="none" aria-hidden="true">
                    <rect x="1" y="1" width="218" height="54" rx="27" ry="27" pathLength="100" />
                  </svg>
                  <button
                    type="button"
                    class="cta-placeholder"
                    :class="{ 'cta-placeholder--configured': hasConfiguredCta }"
                    :style="previewCtaStyle"
                    aria-label="Set voucher redemption"
                    @click="openButtonStyleModal"
                  >
                    {{ previewCtaLabel }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="showMessageModal"
        class="modal-overlay section-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Edit prize message"
        @click.self="closeMessageModal"
      >
        <div class="modal-container section-modal-content">
          <header class="modal-header section-modal-header">
            <h4>Edit Prize Page Message</h4>
            <button
              type="button"
              class="modal-close-button"
              aria-label="Close prize message editor"
              @click="closeMessageModal"
            >
              &times;
            </button>
          </header>
          <div class="modal-body section-modal-body">
            <label class="message-label" for="message-editor-input">Type in your prize page messaging and instructions here</label>
            <textarea
              id="message-editor-input"
              class="message-textarea"
              :value="draftMessage"
              :maxlength="message.maxLength"
              :aria-describedby="messageHelperId"
              :aria-invalid="Boolean(messageError)"
              aria-label="Prize message input"
              @input="handleMessageInput"
            ></textarea>
            <div class="message-meta" :id="messageHelperId">
              <span v-if="messageError" class="error-text" role="status" aria-live="polite">
                {{ messageError }}
              </span>
              <span v-else class="message-hint">Keep it simple, friendly, and clear.</span>
              <span
                class="message-count"
                :class="{
                  'message-count--near': isNearLimit,
                  'message-count--limit': isAtLimit
                }"
              >
                {{ draftMessage.length }}/{{ message.maxLength }}
              </span>
            </div>
            <div class="section-modal-actions">
              <button type="button" class="secondary-button" @click="closeMessageModal">Cancel</button>
              <button type="button" class="generate-button" :disabled="Boolean(messageError)" @click="saveMessage">
                Save Message
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="showVoucherModal"
        class="modal-overlay section-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Edit voucher setup"
        @click.self="closeVoucherModal"
      >
        <div class="modal-container section-modal-content">
          <header class="modal-header section-modal-header">
            <h4>Voucher setup</h4>
            <button
              type="button"
              class="modal-close-button"
              aria-label="Close voucher setup"
              @click="closeVoucherModal"
            >
              &times;
            </button>
          </header>
          <div class="modal-body section-modal-body">
            <div class="upload-form">
              <p class="upload-note">
                Decide your voucher quantities, length of voucher code and any special prefix below
              </p>

              <div class="upload-actions">
                <button type="button" class="secondary-button secondary-button--filled" @click="triggerFileInput">
                  Add your own voucher file here
                </button>
                <input
                  ref="fileInput"
                  class="file-input"
                  type="file"
                  accept=".csv,text/csv"
                  @change="handleUploadFile"
                />
              </div>

              <button type="button" class="secondary-button secondary-button--filled generate-csv-trigger" @click="openGenerateModal">
                Generate your own vouchers here
              </button>

              <p v-if="mode === 'generated' && generatedVouchers.length > 0" class="info-text">
                Using generated vouchers.
              </p>
              <p v-if="uploadedConfig" class="success-text">
                Loaded {{ uploadedConfig.rowCount }} vouchers from {{ uploadedConfig.fileName }}.
              </p>
              <p v-if="uploadError" class="error-text">{{ uploadError }}</p>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="showGenerateModal"
        class="modal-overlay vouchers-generate-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Generate voucher CSV"
        @click.self="closeGenerateModal"
      >
        <div class="modal-container generate-modal-content">
          <header class="modal-header generate-modal-header">
            <h4>Generate your own vouchers</h4>
            <button
              type="button"
              class="modal-close-button"
              aria-label="Close voucher generator"
              @click="closeGenerateModal"
            >
              &times;
            </button>
          </header>

          <div class="modal-body generate-modal-body">
            <p class="upload-note">Decide your voucher quantities, length of voucher code and any special prefix below</p>

            <div class="generator-form">
              <div class="field-group">
                <label for="voucher-quantity">Select Quantity</label>
                <select
                  id="voucher-quantity"
                  :value="generatedConfig.quantity"
                  @change="generatedConfig.quantity = Number(($event.target as HTMLSelectElement).value); emitCurrentConfig()"
                >
                  <option value="" disabled>select from drop down</option>
                  <option v-for="n in [10, 25, 50, 100, 200, 500, 1000]" :key="n" :value="n">{{ n }}</option>
                </select>
              </div>

              <div class="field-group">
                <label for="voucher-code-length">Code Length <span class="field-hint">(Excluding prefix)</span></label>
                <select
                  id="voucher-code-length"
                  :value="generatedConfig.codeLength"
                  @change="generatedConfig.codeLength = Number(($event.target as HTMLSelectElement).value); emitCurrentConfig()"
                >
                  <option value="" disabled>select from drop down</option>
                  <option v-for="n in 13" :key="n + 3" :value="n + 3">{{ n + 3 }}</option>
                </select>
              </div>

              <div class="field-group">
                <label for="voucher-prefix">Code Prefix <span class="field-hint">(Choose a prefix word or leave blank)</span></label>
                <input
                  id="voucher-prefix"
                  :value="generatedConfig.codePrefix"
                  type="text"
                  maxlength="8"
                  placeholder="prefix word"
                  @input="handlePrefixInput"
                />
              </div>
            </div>

            <div class="generate-modal-actions">
              <button type="button" class="generate-button" @click="generateVouchers">
                Generate Voucher Codes
              </button>
            </div>
            <p v-if="generationError" class="error-text">{{ generationError }}</p>
          </div>
        </div>
      </div>
    </teleport>

    <ButtonStyleModal
      :show="showButtonStyleModal"
      title="Configure Prize Redemption"
      description="Add a link to where the vouchers can be redeemed and decide on the button colour configuration"
      :config="buttonConfig"
      :fallback-config="fallbackButtonConfig"
      :allow-url="true"
      :fallback-background-color="brandColors.secondary"
      :fallback-text-color="brandColors.buttonText"
      :preview-background-color="brandColors.background"
      :preview-background-image-url="backgroundPreviewUrl"
      @close="closeButtonStyleModal"
      @save="saveButtonStyle"
    />
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref, watch, type StyleValue } from 'vue'
import { useUIStore } from '@/store/ui.store'
import type {
  CtaButtonConfig,
  GeneratedVoucherConfig,
  PrizeMessageState,
  StyledButtonConfig,
  VoucherConfig,
  VoucherMode
} from '@/types/form.types'
import ButtonStyleModal from '@/components/ButtonStyleModal.vue'
import { DEFAULT_BUTTON_CONFIGS, buildButtonPreviewStyle } from '@/utils/buttonStyles'
import { isButtonConfigCustomized } from '@/utils/previewCustomization'

const props = defineProps<{
  voucherConfig?: VoucherConfig | null
  ctaConfig?: CtaButtonConfig | null
  buttonConfig?: StyledButtonConfig | null
  message: PrizeMessageState
  logoPreviewUrl?: string | null
  backgroundPreviewUrl?: string | null
  defaultLogo: string
  brandColors: {
    primary: string
    secondary: string
    background: string
    header: string
    buttonText: string
  }
  previewButtonStyle: StyleValue
}>()

const emit = defineEmits<{
  (e: 'update-voucher-config', value: VoucherConfig): void
  (e: 'update-cta-config', value: CtaButtonConfig | undefined): void
  (e: 'update-button-config', value: StyledButtonConfig): void
  (e: 'update-message', value: string): void
  (e: 'open-logo-customizer'): void
}>()
const uiStore = useUIStore()

const DEFAULT_GENERATED_CONFIG: GeneratedVoucherConfig = {
  voucherName: '',
  voucherType: 'single-use',
  validUntil: null,
  codePrefix: '',
  quantity: 25,
  codeLength: 8
}

const PROFANITY_WORDS = [
  'fuck',
  'shit',
  'bitch',
  'asshole',
  'bastard',
  'cunt',
  'dick',
  'piss',
  'slut',
  'whore',
  'damn',
  'crap'
]

const POLITICAL_TERMS = [
  'politics',
  'political',
  'election',
  'vote',
  'democrat',
  'republican',
  'liberal',
  'conservative',
  'leftwing',
  'rightwing',
  'trump',
  'biden',
  'maga'
]

const BANNED_PHRASES = [
  'make america great again',
  'black lives matter',
  'blue lives matter',
  'stop the steal'
]

const BANNED_TERMS = [...PROFANITY_WORDS, ...POLITICAL_TERMS]

const mode = ref<VoucherMode>('upload')
const generationError = ref('')
const uploadError = ref('')
const showVoucherModal = ref(false)
const showGenerateModal = ref(false)
const showMessageModal = ref(false)
const showButtonStyleModal = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)
const generatedConfig = reactive<GeneratedVoucherConfig>({ ...DEFAULT_GENERATED_CONFIG })
const generatedVouchers = ref<string[]>([])
const uploadedVouchers = ref<string[]>([])
const uploadedConfig = ref<VoucherConfig['uploadedConfig']>()
const draftMessage = ref('')
const messageError = ref('')
const messageHelperId = 'end-journey-prize-message-helper'
const isAnyLocalModalOpen = computed(
  () => showVoucherModal.value || showGenerateModal.value || showMessageModal.value || showButtonStyleModal.value
)
const fallbackButtonConfig = DEFAULT_BUTTON_CONFIGS.prize

const sanitizePrefix = (value: string) => value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)

const sanitizeMessage = (value: string) => value.replace(/\r/g, '').replace(/[ \t]{2,}/g, ' ')

const escapeRegExp = (value: string) => {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

const findBlockedTerm = (value: string) => {
  const normalized = value.toLowerCase()
  for (const term of BANNED_TERMS) {
    const pattern = new RegExp(`\\b${escapeRegExp(term)}\\b`, 'i')
    if (pattern.test(normalized)) {
      return term
    }
  }
  for (const phrase of BANNED_PHRASES) {
    const pattern = new RegExp(`\\b${escapeRegExp(phrase).replace(/\\\s+/g, '\\s+')}\\b`, 'i')
    if (pattern.test(normalized)) {
      return phrase
    }
  }
  return null
}

const normalizeNumber = (value: number, min: number, max: number, fallback: number) => {
  if (!Number.isFinite(value)) {
    return fallback
  }
  return Math.min(max, Math.max(min, Math.round(value)))
}

const randomToken = (length: number) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let token = ''
  for (let index = 0; index < length; index += 1) {
    token += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return token
}

const activeVouchers = computed(() =>
  mode.value === 'generated' ? generatedVouchers.value : uploadedVouchers.value
)

const hasConfiguredVouchers = computed(() => activeVouchers.value.length > 0)
const hasCustomMessage = computed(() => props.message.text.trim().length > 0)
const hasCustomizedLogo = computed(() => Boolean(props.logoPreviewUrl?.trim()))

const resolvedLogoSrc = computed(() => props.logoPreviewUrl || props.defaultLogo)

const previewMessage = computed(() => {
  const messageText = props.message.text.trim()
  return messageText || 'Tap to add your prize message'
})

const previewVoucherSummary = computed(() => {
  if (activeVouchers.value.length === 0) {
    return 'Tap to upload or generate vouchers'
  }
  if (activeVouchers.value.length === 1) {
    return activeVouchers.value[0]
  }
  return `${activeVouchers.value[0]}\n(+${activeVouchers.value.length - 1} more)`
})

const hasConfiguredCta = computed(() => Boolean(props.buttonConfig?.url?.trim() || props.ctaConfig?.url?.trim()))
const hasCustomizedPrizeButton = computed(() => hasConfiguredCta.value || isButtonConfigCustomized('prize', props.buttonConfig))
const previewCtaLabel = computed(() => {
  const configuredLabel = props.buttonConfig?.label?.trim() || props.ctaConfig?.label?.trim()
  if (configuredLabel) {
    return configuredLabel
  }
  return 'Set voucher\nredemption here'
})

const phoneScreenStyle = computed(() => ({
  backgroundColor: props.brandColors.background,
  backgroundImage: props.backgroundPreviewUrl ? `url('${props.backgroundPreviewUrl}')` : undefined,
  backgroundSize: props.backgroundPreviewUrl ? 'cover' : undefined,
  backgroundPosition: props.backgroundPreviewUrl ? 'center' : undefined,
  backgroundRepeat: props.backgroundPreviewUrl ? 'no-repeat' : undefined,
  color: props.brandColors.header
}))

const phoneMessageStyle = computed(() => ({
  color: props.brandColors.header
}))

const previewCtaStyle = computed(() => ({
  ...buildButtonPreviewStyle(props.buttonConfig, {
    backgroundColor: props.brandColors.secondary,
    textColor: props.brandColors.buttonText,
    borderColor: props.brandColors.secondary,
  }, DEFAULT_BUTTON_CONFIGS.prize),
  opacity: hasConfiguredCta.value ? '1' : '0.8',
  cursor: 'pointer'
}))

const isNearLimit = computed(() => {
  if (!props.message.maxLength) {
    return false
  }
  return draftMessage.value.length >= Math.floor(props.message.maxLength * 0.9)
})

const isAtLimit = computed(() => {
  if (!props.message.maxLength) {
    return false
  }
  return draftMessage.value.length >= props.message.maxLength
})

const emitCurrentConfig = () => {
  emit('update-voucher-config', {
    mode: mode.value,
    generatedConfig: {
      ...generatedConfig,
      codePrefix: sanitizePrefix(generatedConfig.codePrefix),
      quantity: normalizeNumber(generatedConfig.quantity, 1, 1000, DEFAULT_GENERATED_CONFIG.quantity),
      codeLength: normalizeNumber(generatedConfig.codeLength, 4, 16, DEFAULT_GENERATED_CONFIG.codeLength)
    },
    uploadedConfig: mode.value === 'upload' ? uploadedConfig.value : undefined,
    vouchers: mode.value === 'generated' ? generatedVouchers.value : uploadedVouchers.value
  })
}

const applyIncomingConfig = (config?: VoucherConfig | null) => {
  const source = config ?? {
    mode: 'generated' as const,
    generatedConfig: DEFAULT_GENERATED_CONFIG,
    vouchers: []
  }
  const sourceGeneratedConfig = source.generatedConfig ?? DEFAULT_GENERATED_CONFIG
  const resolvedMode: VoucherMode = source.mode === 'upload' && (source.vouchers?.length ?? 0) > 0
    ? 'upload'
    : 'generated'

  mode.value = resolvedMode
  generatedConfig.voucherName = sourceGeneratedConfig.voucherName ?? DEFAULT_GENERATED_CONFIG.voucherName
  generatedConfig.voucherType = sourceGeneratedConfig.voucherType ?? DEFAULT_GENERATED_CONFIG.voucherType
  generatedConfig.validUntil = sourceGeneratedConfig.validUntil ?? DEFAULT_GENERATED_CONFIG.validUntil
  generatedConfig.codePrefix = sanitizePrefix(sourceGeneratedConfig.codePrefix ?? DEFAULT_GENERATED_CONFIG.codePrefix)
  generatedConfig.quantity = normalizeNumber(
    sourceGeneratedConfig.quantity ?? DEFAULT_GENERATED_CONFIG.quantity,
    1,
    1000,
    DEFAULT_GENERATED_CONFIG.quantity
  )
  generatedConfig.codeLength = normalizeNumber(
    sourceGeneratedConfig.codeLength ?? DEFAULT_GENERATED_CONFIG.codeLength,
    4,
    16,
    DEFAULT_GENERATED_CONFIG.codeLength
  )

  generatedVouchers.value = resolvedMode === 'generated' ? source.vouchers ?? [] : []
  uploadedVouchers.value = resolvedMode === 'upload' ? source.vouchers ?? [] : []
  uploadedConfig.value = source.uploadedConfig
}

watch(
  () => props.voucherConfig,
  (config) => {
    applyIncomingConfig(config)
  },
  { immediate: true, deep: true }
)

watch(
  () => props.message.text,
  (newValue) => {
    if (!showMessageModal.value) {
      draftMessage.value = newValue
    }
  },
  { immediate: true }
)

onMounted(() => {
  if (mode.value === 'generated' && generatedVouchers.value.length === 0) {
    generateVouchers()
  }
})

const openMessageModal = () => {
  draftMessage.value = props.message.text
  messageError.value = ''
  showMessageModal.value = true
}

const closeMessageModal = () => {
  showMessageModal.value = false
}

const normalizeDraftMessage = (value: string) => {
  const sanitized = sanitizeMessage(value)
  if (!props.message.maxLength) {
    return sanitized
  }
  return sanitized.slice(0, props.message.maxLength)
}

const handleMessageInput = (event: Event) => {
  const target = event.target as HTMLTextAreaElement
  const normalized = normalizeDraftMessage(target.value)
  draftMessage.value = normalized
  target.value = normalized
  messageError.value = findBlockedTerm(normalized)
    ? 'Please remove profanity or politically charged content.'
    : ''
}

const saveMessage = () => {
  const normalized = normalizeDraftMessage(draftMessage.value)
  const blockedTerm = findBlockedTerm(normalized)
  if (blockedTerm) {
    messageError.value = 'Please remove profanity or politically charged content.'
    return
  }
  messageError.value = ''
  emit('update-message', normalized)
  closeMessageModal()
}

const normalizeCta = (value?: CtaButtonConfig | null): CtaButtonConfig | undefined => {
  if (!value?.url) {
    return undefined
  }
  const url = value.url.trim()
  if (!url) {
    return undefined
  }
  const label = value.label?.trim()
  return {
    url,
    target: '_blank',
    label: label || undefined
  }
}

const openButtonStyleModal = () => {
  showButtonStyleModal.value = true
}

const closeButtonStyleModal = () => {
  showButtonStyleModal.value = false
}

const saveButtonStyle = (value: StyledButtonConfig) => {
  emit('update-button-config', value)
  emit('update-cta-config', normalizeCta(value.url ? {
    url: value.url,
    target: value.target,
    label: value.label
  } : undefined))
  closeButtonStyleModal()
}

watch(
  isAnyLocalModalOpen,
  (isOpen) => {
    if (isOpen) {
      uiStore.openModal()
    } else {
      uiStore.closeModal()
    }
  }
)

onUnmounted(() => {
  if (isAnyLocalModalOpen.value) {
    uiStore.closeModal()
  }
})

const openVoucherModal = () => {
  uploadError.value = ''
  showVoucherModal.value = true
}

const closeVoucherModal = () => {
  showVoucherModal.value = false
}

const openGenerateModal = () => {
  generationError.value = ''
  showGenerateModal.value = true
}

const closeGenerateModal = () => {
  showGenerateModal.value = false
}

const handlePrefixInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  const sanitized = sanitizePrefix(target.value)
  target.value = sanitized
  generatedConfig.codePrefix = sanitized
  emitCurrentConfig()
}

const generateVouchers = () => {
  generationError.value = ''
  const quantity = normalizeNumber(generatedConfig.quantity, 1, 1000, DEFAULT_GENERATED_CONFIG.quantity)
  const codeLength = normalizeNumber(generatedConfig.codeLength, 4, 16, DEFAULT_GENERATED_CONFIG.codeLength)
  const prefix = sanitizePrefix(generatedConfig.codePrefix)

  generatedConfig.quantity = quantity
  generatedConfig.codeLength = codeLength
  generatedConfig.codePrefix = prefix

  const vouchers = new Set<string>()
  const maxAttempts = quantity * 10
  let attempts = 0

  while (vouchers.size < quantity && attempts < maxAttempts) {
    vouchers.add(`${prefix}${randomToken(codeLength)}`)
    attempts += 1
  }

  if (vouchers.size !== quantity) {
    generationError.value = 'Could not generate enough unique vouchers. Try changing prefix or code length.'
    return
  }

  mode.value = 'generated'
  generatedVouchers.value = Array.from(vouchers)
  emitCurrentConfig()
  closeGenerateModal()
  closeVoucherModal()
}

const triggerFileInput = () => {
  fileInput.value?.click()
}

const parseCsvVouchers = (content: string) => {
  const normalized = content.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n')
  const lines = normalized.split('\n').map((line) => line.trim()).filter(Boolean)

  if (lines.length < 2) {
    throw new Error('CSV must include a header and at least one voucher row.')
  }

  const headerColumns = lines[0].split(',').map((value) => value.trim())
  if (headerColumns.length !== 1 || headerColumns[0] !== 'Vouchers') {
    throw new Error('Invalid header. Expected one column with header "Vouchers".')
  }

  const vouchers: string[] = []
  for (const row of lines.slice(1)) {
    const columns = row.split(',').map((value) => value.trim())
    if (columns.length !== 1) {
      throw new Error('Each row must contain exactly one value under the "Vouchers" column.')
    }
    if (columns[0]) {
      vouchers.push(columns[0])
    }
  }

  if (vouchers.length === 0) {
    throw new Error('No vouchers found in CSV.')
  }

  return vouchers
}

const handleUploadFile = (event: Event) => {
  uploadError.value = ''
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) {
    return
  }

  if (!file.name.toLowerCase().endsWith('.csv')) {
    uploadError.value = 'Please select a .csv file.'
    target.value = ''
    return
  }

  const reader = new FileReader()
  reader.onload = () => {
    try {
      const content = String(reader.result ?? '')
      const vouchers = parseCsvVouchers(content)
      mode.value = 'upload'
      uploadedVouchers.value = vouchers
      uploadedConfig.value = {
        fileName: file.name,
        header: 'Vouchers',
        rowCount: vouchers.length
      }
      emitCurrentConfig()
      closeVoucherModal()
    } catch (error) {
      uploadError.value = error instanceof Error ? error.message : 'Failed to parse CSV file.'
      uploadedVouchers.value = []
      uploadedConfig.value = undefined
      emitCurrentConfig()
    }
    target.value = ''
  }
  reader.readAsText(file)
}
</script>

<style scoped>
.vouchers-section {
  justify-content: flex-start;
  align-items: stretch;
  padding-top: var(--section-padding-block-start);
  overflow: visible;
}

.vouchers-scroll-shell {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: stretch;
  min-height: 0;
}

.vouchers-scroll-region {
  width: min(100%, var(--base-layout-section-max-width));
  max-height: none;
  overflow: visible;
  padding: 0 0.1rem calc(var(--control-nav-size) + 3rem + env(safe-area-inset-bottom, 0px));
}

.vouchers-content .section-header h3 {
  font-size: var(--base-layout-section-heading-h3-size);
}

.vouchers-content .section-header {
  text-align: center;
  margin: 0 auto;
  width: min(100%, var(--base-layout-section-max-width));
}

.vouchers-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--section-content-gap);
  width: 100%;
  max-width: 100%;
  margin-inline: auto;
}

.phone-preview-shell {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: var(--campaign-preview-phone-shell-margin-top);
  padding-bottom: var(--campaign-preview-phone-shell-padding-bottom);
}

.phone-preview {
  --preview-density-scale: var(--campaign-preview-density-scale);
  width: var(--campaign-preview-phone-width);
  max-width: 100%;
  background: #12162a;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: var(--campaign-preview-phone-radius);
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.35);
  padding: var(--campaign-preview-phone-padding);
}

.phone-speaker {
  width: 34%;
  height: calc(var(--campaign-preview-phone-speaker-height) * var(--preview-density-scale));
  margin: 0 auto var(--campaign-preview-phone-speaker-margin-bottom);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.25);
}

.phone-screen {
  --editable-target-border: rgba(122, 230, 177, 0.82);
  --editable-target-border-strong: rgb(122, 230, 177);
  --editable-target-surface: rgba(9, 18, 33, 0.26);
  --editable-target-radius: 14px;
  border-radius: var(--campaign-preview-phone-screen-radius);
  height: var(--campaign-preview-phone-screen-height);
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding:
    calc(var(--campaign-preview-phone-screen-padding-block) * var(--preview-density-scale))
    calc(var(--campaign-preview-phone-screen-padding-inline) * var(--preview-density-scale))
    calc((var(--campaign-preview-phone-screen-padding-block) + 0.35rem) * var(--preview-density-scale));
  display: grid;
  grid-template-rows:
    var(--campaign-preview-phone-logo-height)
    minmax(0, 1.35fr)
    minmax(0, 0.8fr)
    auto;
  gap: calc(var(--campaign-preview-phone-gap) * var(--preview-density-scale));
  justify-items: center;
  align-content: space-between;
  align-items: stretch;
  overflow: hidden;
}

.phone-screen > .tap-target,
.phone-screen > .cta-target {
  min-height: 0;
  box-sizing: border-box;
  justify-self: center;
}

.tap-target {
  width: 100%;
  margin-inline: 0;
  border: none;
  border-radius: var(--editable-target-radius) !important;
  background: transparent;
  color: inherit;
  text-align: left;
  padding:
    calc(var(--campaign-preview-phone-target-padding-block) * var(--preview-density-scale))
    calc(var(--campaign-preview-phone-target-padding-inline) * var(--preview-density-scale));
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow: none;
  transform: none;
}

.marching-ants {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.marching-ants rect {
  fill: none;
  stroke: var(--editable-target-border-strong);
  stroke-width: 1.6;
  stroke-dasharray: 2.2 2.2;
  stroke-linecap: round;
  vector-effect: non-scaling-stroke;
  animation: marching-ants-offset 1.2s linear infinite;
}

.tap-target:hover {
  border-color: var(--editable-target-border-strong);
  box-shadow: none;
  transform: none;
}

.tap-target:focus,
.tap-target:focus-visible,
.tap-target:active {
  border-color: var(--editable-target-border-strong);
  box-shadow: none;
  transform: none;
}

.logo-target {
  grid-row: 1;
  align-self: center;
  /* Larger than default so the brand reads clearly in the rewards
     preview — matches the bump applied in the AR preview section. */
  width: calc(var(--campaign-preview-phone-logo-width) * 1.2);
  height: calc(var(--campaign-preview-phone-logo-height) * 1.35);
  min-height: calc(var(--campaign-preview-phone-logo-height) * 1.35);
  aspect-ratio: auto;
  border-radius: var(--editable-target-radius) !important;
  padding: calc(var(--campaign-preview-phone-logo-padding) * var(--preview-density-scale));
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  line-height: 0;
}

.message-target,
.voucher-target,
.cta-target {
  align-self: center;
  width: var(--campaign-preview-phone-panel-width);
  border-radius: var(--editable-target-radius) !important;
  overflow: hidden;
}

/* Prize message + voucher text boxes mirror the LookFeel pattern: the white
   75% pane is painted by a smaller-inset ::before pseudo, and the
   marching-ants stay at their natural inset: 0 on the parent — so the ants
   reliably appear just outside the pane edge without needing negative
   insets + width: auto (which iOS Safari mishandles). */
.phone-preview .message-target,
.phone-preview .voucher-target {
  background: transparent !important;
  overflow: visible !important;
  position: relative;
}

.phone-preview .message-target::before,
.phone-preview .voucher-target::before {
  content: '';
  position: absolute;
  top: calc(0.4rem * var(--preview-density-scale));
  right: calc(0.4rem * var(--preview-density-scale));
  bottom: calc(0.4rem * var(--preview-density-scale));
  left: calc(0.4rem * var(--preview-density-scale));
  background: rgba(255, 255, 255, 0.75);
  border-radius: calc(var(--editable-target-radius) * 0.75);
  pointer-events: none;
  z-index: 0;
}

/* Keep text / SVG on top of the pane. */
.phone-preview .message-target > :not(.marching-ants),
.phone-preview .voucher-target > :not(.marching-ants) {
  position: relative;
  z-index: 1;
}

.phone-logo {
  width: auto;
  height: auto;
  object-fit: contain;
  object-position: center;
  display: block;
  max-width: var(--campaign-preview-phone-logo-max-size);
  max-height: var(--campaign-preview-phone-logo-max-size);
  flex-shrink: 0;
}

.message-target {
  grid-row: 2;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  padding:
    calc(clamp(1.35rem, 3.4vw, 1.9rem) * var(--preview-density-scale))
    calc(clamp(1.05rem, 2.8vw, 1.45rem) * var(--preview-density-scale));
}

.preview-message {
  margin: 0;
  display: block;
  width: 100%;
  font-size: clamp(0.78rem, calc(0.72rem + 0.4vw), 1.02rem);
  line-height: 1.35;
  white-space: pre-wrap;
  text-align: center;
  padding:
    calc(clamp(0.8rem, 2vw, 1.1rem) * var(--preview-density-scale))
    calc(clamp(0.75rem, 1.8vw, 1rem) * var(--preview-density-scale));
  box-sizing: border-box;
  max-width: 100%;
  word-break: break-word;
  overflow-wrap: anywhere;
  hyphens: auto;
}

.message-target--placeholder {
  justify-content: center;
  text-align: center;
}

.preview-message--placeholder {
  text-align: center;
}

.voucher-target {
  grid-row: 3;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
}

.preview-voucher {
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: clamp(0.72rem, calc(0.66rem + 0.32vw), 0.92rem);
  line-height: 1.35;
  word-break: break-word;
  white-space: pre-line;
  text-align: center;
}

.voucher-target--placeholder {
  justify-content: center;
  text-align: center;
}

.preview-voucher--placeholder {
  text-align: center;
}

.cta-target {
  grid-row: 4;
  align-self: end;
  position: relative;
  border-radius: 999px !important;
  background: transparent;
  height: auto;
  min-height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--campaign-preview-phone-button-width);
  padding: var(--campaign-preview-phone-button-shell-padding);
  overflow: visible;
  box-sizing: border-box;
}

.cta-placeholder {
  margin: 0;
  width: 100%;
  max-width: 100%;
  border-radius: inherit;
  min-height: var(--campaign-preview-phone-button-min-height);
  padding:
    var(--campaign-preview-phone-button-padding-block)
    var(--campaign-preview-phone-button-padding-inline);
  box-sizing: border-box;
  border: 0;
  font-weight: 700;
  font-size: 0.7rem;
  white-space: normal;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  box-shadow: none;
  transform: none;
  transition: opacity 0.2s ease;
}

.cta-target .marching-ants {
  inset: 1px;
}

@keyframes marching-ants-offset {
  to {
    stroke-dashoffset: -8.8;
  }
}


.cta-placeholder:hover,
.cta-placeholder:focus,
.cta-placeholder:focus-visible,
.cta-placeholder:active {
  box-shadow: none;
  transform: none;
}

.cta-placeholder--configured {
  opacity: 1;
}

.cta-fields {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.section-modal,
.vouchers-generate-modal {
  z-index: calc(var(--base-layout-modal-z-index) + 20);
}

.section-modal-content,
.generate-modal-content {
  width: 100%;
  max-width: min(100%, 760px);
  max-height: min(90vh, 760px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--ui-inverse-surface) !important;
  border: 1px solid rgba(148, 163, 184, 0.24) !important;
}

.section-modal-header,
.generate-modal-header {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  position: relative;
  padding: 1rem 3.25rem;
  background: var(--ui-inverse-surface) !important;
}

.section-modal-header h4,
.generate-modal-header h4 {
  margin: 0;
  color: #ffffff !important;
  text-align: center;
  font-family: var(--ui-inverse-font-family);
  font-weight: 800;
}

.section-modal-content .section-modal-header h4,
.generate-modal-content .generate-modal-header h4 {
  color: #ffffff !important;
}

.section-modal-body,
.generate-modal-body {
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  background: var(--ui-inverse-surface) !important;
}

.section-modal-actions,
.generate-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.message-label {
  font-size: var(--base-layout-form-label-size);
  font-weight: 700;
  color: #ffffff !important;
  font-family: var(--ui-inverse-font-family);
}

.message-textarea {
  width: 100%;
  min-height: clamp(7rem, 18vh, 12rem);
  padding: var(--base-layout-form-input-padding);
  border-radius: 0.6rem !important;
  border: var(--base-layout-form-input-border-width) solid var(--color-border);
  background-color: var(--color-background-mute);
  color: var(--color-text);
  font-size: var(--base-layout-form-input-font-size);
  font-family: var(--ui-inverse-font-family);
  resize: vertical;
}

.modal-overlay .modal-container .message-textarea {
  border-radius: 0.6rem !important;
}

.message-textarea:focus {
  outline: none;
  border-color: var(--vt-c-accent);
  box-shadow: 0 0 0 var(--base-layout-form-input-focus-ring-size) var(--base-layout-form-input-focus-ring-color);
  background-color: var(--color-background-secondary);
}

.message-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--base-layout-spacing-md);
  color: var(--ui-inverse-text-muted);
  opacity: 1;
  font-size: var(--base-layout-message-font-size);
}

.message-hint {
  color: #ffffff !important;
  opacity: 1;
  font-family: var(--ui-inverse-font-family);
}

#end-journey-cta-helper {
  justify-content: center;
  text-align: center;
}

.message-count {
  font-weight: 600;
  color: #ffffff !important;
  opacity: 1;
}

.message-count--near {
  color: #fbbf24;
  opacity: 1;
}

.message-count--limit {
  color: #8f1434;
  opacity: 1;
}

.generator-form {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.7rem;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.7rem;
  border: 1px solid var(--ui-inverse-border);
  border-radius: 0.75rem;
  background: rgba(16, 26, 52, 0.08);
}

.field-group label {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--ui-inverse-text);
  font-family: var(--ui-inverse-font-family);
}

.field-hint {
  font-weight: 400;
  font-size: 0.8rem;
  opacity: 0.7;
}

.field-group input,
.field-group select {
  margin: 0;
  min-height: 2.6rem;
  border-radius: 999px;
  border: 1px solid var(--ui-inverse-control-border);
  background-color: var(--ui-inverse-control-bg);
  color: var(--ui-inverse-control-text);
  text-align: center;
  font-family: var(--ui-inverse-font-family);
}

.field-group input:focus,
.field-group select:focus {
  outline: none;
  border-color: rgba(16, 26, 52, 0.4);
  box-shadow: 0 0 0 3px rgba(16, 26, 52, 0.12);
}

.field-group input::placeholder {
  color: var(--ui-inverse-control-placeholder);
}

.field-group select option {
  color: var(--ui-inverse-control-text);
  background: #16233f;
}

.field-group select {
  text-align-last: center;
}

.generate-button {
  margin: 0;
  border: 1px solid var(--vt-c-accent) !important;
  background: var(--vt-c-accent) !important;
  color: var(--color-background) !important;
  border-radius: 999px;
  padding: 0.65rem 1rem;
  font-family: var(--ui-inverse-font-family);
  font-weight: 700;
}

.generate-button:hover:not(:disabled) {
  background: var(--ui-inverse-cta-bg-hover) !important;
  border-color: var(--ui-inverse-cta-bg-hover) !important;
  color: var(--color-background) !important;
  transform: none;
}

.section-modal-content .generate-button,
.section-modal-content .secondary-button,
.generate-modal-content .generate-button,
.generate-modal-content .secondary-button {
  background: var(--vt-c-accent) !important;
  color: var(--color-background) !important;
  border: 1px solid var(--vt-c-accent) !important;
}

.section-modal-actions .secondary-button,
.section-modal-actions .generate-button,
.generate-modal-actions .secondary-button,
.generate-modal-actions .generate-button {
  background: var(--vt-c-accent) !important;
  color: var(--color-background) !important;
  border: 1px solid var(--vt-c-accent) !important;
}

.section-modal-content .generate-button:hover:not(:disabled),
.section-modal-content .secondary-button:hover,
.generate-modal-content .generate-button:hover:not(:disabled),
.generate-modal-content .secondary-button:hover {
  background: var(--ui-inverse-cta-bg-hover) !important;
  color: var(--color-background) !important;
  border-color: var(--ui-inverse-cta-bg-hover) !important;
}

.upload-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.upload-note {
  margin: 0;
  color: var(--ui-inverse-text-muted);
  font-family: var(--ui-inverse-font-family);
  text-align: center;
}

.upload-actions {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.generate-csv-trigger {
  align-self: flex-start;
}

.secondary-button {
  margin: 0;
  border: 1px solid var(--vt-c-accent) !important;
  background: var(--vt-c-accent) !important;
  color: var(--color-background) !important;
  border-radius: 999px;
  padding: 0.55rem 0.95rem;
  cursor: pointer;
  font-weight: 700;
  font-family: var(--ui-inverse-font-family);
}

.secondary-button:hover {
  background: var(--ui-inverse-cta-bg-hover) !important;
  border-color: var(--ui-inverse-cta-bg-hover) !important;
  color: var(--color-background) !important;
  transform: none;
}

.secondary-button--filled {
  background: var(--vt-c-accent);
  color: var(--color-background);
  border-color: var(--vt-c-accent);
}

.secondary-button--filled:hover {
  background: var(--ui-inverse-cta-bg-hover);
  border-color: var(--ui-inverse-cta-bg-hover);
  color: var(--color-background);
}

.info-text {
  margin: 0;
  color: var(--ui-inverse-text);
  font-weight: 600;
  font-family: var(--ui-inverse-font-family);
}

.file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.error-text {
  margin: 0;
  color: #8f1434;
  font-weight: 600;
  font-family: var(--ui-inverse-font-family);
}

.success-text {
  margin: 0;
  color: var(--ui-inverse-text);
  font-weight: 600;
  font-family: var(--ui-inverse-font-family);
}

@media (min-width: 769px) and (max-width: 1439px) {
  .message-target {
    padding:
      calc(clamp(0.88rem, 2vw, 1.18rem) * var(--preview-density-scale))
      calc(clamp(0.7rem, 1.6vw, 0.9rem) * var(--preview-density-scale));
  }

  .preview-message {
    font-size: clamp(0.58rem, calc(0.54rem + 0.12vw), 0.72rem);
    line-height: 1.22;
    padding:
      calc(clamp(0.42rem, 1vw, 0.62rem) * var(--preview-density-scale))
      calc(clamp(0.4rem, 0.9vw, 0.56rem) * var(--preview-density-scale));
  }

  .preview-voucher {
    font-size: clamp(0.54rem, calc(0.5rem + 0.1vw), 0.66rem);
    line-height: 1.22;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    line-clamp: 2;
    -webkit-line-clamp: 2;
  }
}

@media (min-width: 769px) and (max-height: 900px) {
  .message-target {
    padding:
      calc(0.82rem * var(--preview-density-scale))
      calc(0.68rem * var(--preview-density-scale));
  }

  .voucher-target {
    padding:
      calc(0.44rem * var(--preview-density-scale))
      calc(0.56rem * var(--preview-density-scale));
  }

  .preview-message {
    font-size: 0.56rem;
    line-height: 1.18;
    padding:
      calc(0.38rem * var(--preview-density-scale))
      calc(0.36rem * var(--preview-density-scale));
  }

  .preview-voucher {
    font-size: 0.5rem;
    line-height: 1.18;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    line-clamp: 2;
    -webkit-line-clamp: 2;
  }
}

@media (max-width: 768px) {
  .vouchers-scroll-region {
    padding-inline: 0;
    padding-bottom: calc(var(--control-nav-size) + 3rem + env(safe-area-inset-bottom, 0px));
  }

  .vouchers-content {
    gap: 0.85rem;
  }

  .phone-preview {
    --preview-density-scale: var(--campaign-preview-density-scale);
  }

  .phone-screen {
    /* Taller than the default clamp on phones so message + voucher panes
     get more breathing room. Width intentionally left alone. */
    height: calc(var(--campaign-preview-phone-screen-height) * 1.2);
    grid-template-rows:
      var(--campaign-preview-phone-logo-height)
      minmax(0, 1.35fr)
      minmax(0, 0.8fr)
      auto;
    gap: calc(var(--campaign-preview-phone-gap) * var(--preview-density-scale));
  }

  .tap-target,
  .cta-target {
    padding:
      calc(var(--campaign-preview-phone-target-padding-block) * var(--preview-density-scale))
      calc(var(--campaign-preview-phone-target-padding-inline) * var(--preview-density-scale));
  }

  .cta-target {
    width: var(--campaign-preview-phone-button-width);
    padding: var(--campaign-preview-phone-button-shell-padding);
  }

  .message-target,
  .voucher-target {
    width: var(--campaign-preview-phone-panel-width);
  }

  .phone-logo {
    max-width: 96%;
    max-height: 96%;
  }

  .cta-placeholder {
    min-height: var(--campaign-preview-phone-button-min-height);
    padding:
      var(--campaign-preview-phone-button-padding-block)
      var(--campaign-preview-phone-button-padding-inline);
  }

  .preview-voucher {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
  }

  .preview-voucher {
    line-clamp: 2;
    -webkit-line-clamp: 2;
  }

  .section-modal,
  .vouchers-generate-modal {
    padding: var(--mobile-modal-overlay-top-padding) var(--mobile-modal-overlay-side-padding) var(--mobile-modal-overlay-bottom-padding);
  }

  .section-modal-content,
  .generate-modal-content {
    width: 100%;
    max-height: min(var(--mobile-modal-max-height), 720px);
  }

  .vouchers-generate-modal .generate-modal-content {
    max-height: min(var(--mobile-modal-max-height), 640px);
  }

  .section-modal-body,
  .generate-modal-body {
    padding: 0.85rem 0.85rem calc(0.85rem + env(safe-area-inset-bottom, 0px));
  }

  .vouchers-generate-modal .generate-modal-body {
    gap: 0.7rem;
    padding: 0.75rem 0.75rem calc(0.75rem + env(safe-area-inset-bottom, 0px));
  }

  .message-meta {
    flex-direction: column;
    align-items: flex-start;
  }

  .generator-form {
    grid-template-columns: 1fr;
  }

  .vouchers-generate-modal .field-group {
    padding: 0.6rem;
  }
}

@media (max-width: 420px) {
  .vouchers-content .section-header {
    width: min(100%, 18rem);
  }

  .vouchers-content .section-header h3 {
    font-size: clamp(1.18rem, 5.2vw, 1.4rem);
    line-height: 1.15;
  }

  .phone-preview {
    --preview-density-scale: var(--campaign-preview-density-scale);
  }

  .phone-screen {
    /* Push a touch taller again on small iPhones where vertical space
       inside the pre-scaled screen was still feeling squashed. */
    height: calc(var(--campaign-preview-phone-screen-height) * 1.25);
    grid-template-rows:
      var(--campaign-preview-phone-logo-height)
      minmax(0, 1.35fr)
      minmax(0, 0.8fr)
      auto;
    gap: calc(var(--campaign-preview-phone-gap) * var(--preview-density-scale));
    padding:
      calc(var(--campaign-preview-phone-screen-padding-block) * var(--preview-density-scale))
      calc(var(--campaign-preview-phone-screen-padding-inline) * var(--preview-density-scale))
      calc((var(--campaign-preview-phone-screen-padding-block) + 0.2rem) * var(--preview-density-scale));
  }

  .tap-target,
  .cta-target {
    padding:
      calc(var(--campaign-preview-phone-target-padding-block) * var(--preview-density-scale))
      calc(var(--campaign-preview-phone-target-padding-inline) * var(--preview-density-scale));
  }

  .phone-logo {
    max-width: 96%;
    max-height: 96%;
  }

  .message-target,
  .voucher-target,
  .cta-target {
    width: min(92%, 200px);
  }

  .preview-message {
    font-size: clamp(0.56rem, calc(0.53rem + 0.24vw), 0.7rem);
    line-height: 1.25;
  }

  .preview-voucher {
    font-size: clamp(0.52rem, calc(0.49rem + 0.22vw), 0.66rem);
    line-clamp: 2;
    -webkit-line-clamp: 2;
  }

  .cta-placeholder {
    min-height: var(--campaign-preview-phone-button-min-height);
    padding:
      var(--campaign-preview-phone-button-padding-block)
      var(--campaign-preview-phone-button-padding-inline);
    font-size: 0.62rem;
    line-height: 1.05;
  }
}
</style>
